import { useEffect, useState } from 'react';
import { Copy, Link2, Trash2, X } from 'lucide-react';
import { API_BASE_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import type { DisclosureShare } from '../../types/disclosure';

interface EventDisclosureShareModalProps {
  open: boolean;
  eventId: string;
  eventName: string;
  onClose: () => void;
}

function isStrongSharePassword(password: string) {
  if (password.length < 12) return false;
  if (!/[A-Za-z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  return true;
}

export default function EventDisclosureShareModal({
  open,
  eventId,
  eventName,
  onClose,
}: EventDisclosureShareModalProps) {
  const { token } = useAppSelector((s) => s.auth);
  const [password, setPassword] = useState('');
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('14');
  const [includePatientNames, setIncludePatientNames] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [shares, setShares] = useState<DisclosureShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadShares = async () => {
    if (!token) return;
    setLoadingShares(true);
    try {
      const res = await fetch(`${API_BASE_URL}/events/${eventId}/disclosure/shares`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setShares(data.shares || []);
    } catch {
      /* ignore */
    } finally {
      setLoadingShares(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setLabel('');
    setExpiresInDays('14');
    setIncludePatientNames(false);
    setError(null);
    setCreatedLink(null);
    setCopied(false);
    loadShares();
  }, [open, eventId, token]);

  if (!open) return null;

  const shareUrl = (shareToken: string) => `${window.location.origin}/disclosure/${shareToken}`;

  const createShare = async () => {
    if (!token || !isStrongSharePassword(password.trim())) {
      setError('Password must be at least 12 characters and include letters and numbers');
      return;
    }
    const days = Number(expiresInDays);
    if (!Number.isFinite(days) || days < 1 || days > 90) {
      setError('Expiry must be between 1 and 90 days');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/events/${eventId}/disclosure/shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          password: password.trim(),
          label: label.trim() || undefined,
          expiresInDays: days,
          includePatientNames,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create share link');
      const url = shareUrl(data.share.token);
      setCreatedLink(url);
      setPassword('');
      await loadShares();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const revokeShare = async (shareId: string) => {
    if (!token || !confirm('Revoke this share link? Recipients will no longer be able to access the report.')) return;
    try {
      await fetch(`${API_BASE_URL}/events/${eventId}/disclosure/shares/${shareId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadShares();
      if (createdLink) setCreatedLink(null);
    } catch {
      setError('Failed to revoke link');
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-5 py-4 border-b border-border sticky top-0 bg-card">
          <div>
            <div className="flex items-center gap-1.5 text-sm font-bold">
              <Link2 className="w-4 h-4 text-primary" />
              Share disclosure link
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-[320px]">{eventName}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Create a password-protected link for an external company. Copy the URL when it is created —
            it cannot be retrieved again later. Send the password separately.
          </p>

          {createdLink && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
              <p className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                Link created — copy it now
              </p>
              <p className="text-[10px] font-mono break-all text-muted-foreground">{createdLink}</p>
              <p className="text-[10px] text-muted-foreground">
                This full URL is shown only once. After you close this dialog you can only revoke the link.
              </p>
              <button
                type="button"
                onClick={() => copyLink(createdLink)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary"
              >
                <Copy className="w-3 h-3" />
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[10px] uppercase text-muted-foreground">Recipient / company (optional)</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Event insurance partner"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-xs"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase text-muted-foreground">
              Password (min 12 chars, letters + numbers)
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a strong password for the recipient"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-xs"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase text-muted-foreground">Link expires after (days, 1–90)</label>
            <input
              type="number"
              min={1}
              max={90}
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-xs"
            />
          </div>

          <label className="flex items-start gap-2 text-[11px] text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={includePatientNames}
              onChange={(e) => setIncludePatientNames(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Include full patient names (sensitive). Off by default — external reports show initials only
              (e.g. Patient J. S.).
            </span>
          </label>

          {error && (
            <div className="text-[11px] text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={createShare}
            disabled={creating || !isStrongSharePassword(password.trim())}
            className="w-full py-2.5 rounded-xl text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create share link'}
          </button>

          <div className="pt-2 border-t border-border">
            <p className="text-[10px] uppercase text-muted-foreground mb-2">Active links</p>
            {loadingShares ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : shares.length === 0 ? (
              <p className="text-xs text-muted-foreground">No active share links yet.</p>
            ) : (
              <ul className="space-y-2">
                {shares.map((s) => (
                  <li key={s.id} className="flex items-start gap-2 text-xs border border-border rounded-lg p-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{s.label || 'Unnamed link'}</p>
                      <p className="font-mono text-[10px] text-muted-foreground">
                        Token {s.tokenHint || '••••'}
                        {s.includePatientNames ? ' · includes names' : ' · names redacted'}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Created {new Date(s.createdAt).toLocaleDateString()}
                        {s.expiresAt ? ` · Expires ${new Date(s.expiresAt).toLocaleDateString()}` : ''}
                        {s.accessCount ? ` · ${s.accessCount} view(s)` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => revokeShare(s.id)}
                      className="p-1.5 rounded hover:bg-destructive/10 text-destructive shrink-0"
                      title="Revoke link"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
